import { Replay } from '@mui/icons-material';
import { IconButton, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import defaultVideoPlayerStyles from '../../styles';
import { KVDSVideoPlayerProps } from './types';
import { buildKVDSServerUrl } from './util';
import { WHEPClient } from './WHEPClient';

type Props = Omit<KVDSVideoPlayerProps, 'streamType'>;

const KVDSVideoPlayer: React.FunctionComponent<Props> = (props) => {
  const {
    url,
    applicationName: groupName, // we map the applicationName to groupName
    streamName,
    className,
    controls,
    setShowPTZControls,
    retry = true,
    setStreamLoaded,
  } = props;
  const wowza = true;

  const [loading, setLoading] = useState(true);
  const [isStreamAvailable, setIsStreamAvailable] = useState(true);
  const [reload, setReload] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { t } = useTranslation('common');

  useEffect(() => {
    let toggleStreamLoaded: ReturnType<typeof setTimeout>;
    if (!loading) {
      toggleStreamLoaded = setTimeout(() => setStreamLoaded?.(!loading), 700);
    } else {
      setStreamLoaded?.(!loading);
    }
    return () => toggleStreamLoaded && clearTimeout(toggleStreamLoaded);
    // only run this effect when loading changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (!isStreamAvailable && !reload) {
      timeout = setTimeout(() => {
        setReload(true);
      }, 1500);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isStreamAvailable, reload]);

  const clearVideo = (instance: HTMLVideoElement) => {
    if (instance) {
      instance.pause();
      instance.srcObject = null;
      instance.src = '';
      instance.load();
    }
  };

  useEffect(() => {
    setShowPTZControls?.(isStreamAvailable);
  }, [isStreamAvailable, setShowPTZControls]);

  useEffect(() => {
    if (!url || !videoRef.current || (!url.startsWith('http') && !url.startsWith('https'))) {
      setIsStreamAvailable(false);
      return;
    }
    setLoading(true);
    setIsStreamAvailable(true);
    if (reload) {
      setReload(false);
      return;
    }

    const instance = videoRef.current;
    if (!url || !groupName || !streamName) {
      setLoading(false);
      setIsStreamAvailable(false);
      return;
    }
    let whepClient: WHEPClient;
    try {
      whepClient = new WHEPClient({
        video: instance,
        url: buildKVDSServerUrl(url, groupName, streamName),
        onTrack: () => {
          setLoading(false);
          setIsStreamAvailable(true);
        },
        onError: () => {
          setLoading(false);
          setIsStreamAvailable(false);
        },
      });
    } catch (error) {
      console.error('Error in KVDSVideoPlayer', error);
      setLoading(false);
      setIsStreamAvailable(false);
    }

    return () => {
      whepClient?.pc?.close();
      clearVideo(instance);
    };
  }, [groupName, streamName, url, wowza, reload]);

  const classes = defaultVideoPlayerStyles();

  return (
    <div style={{ position: 'relative' }} className={classes.videoWrapper}>
      <video
        className={className ? className : classes.video}
        ref={videoRef}
        playsInline
        autoPlay
        muted
        controls={controls}
      />
      {loading ? (
        <div className={className ? className : classes.loading}>
          <CircularProgress />
        </div>
      ) : !isStreamAvailable ? (
        <div className={className ? className : classes.unavailableWrapper}>
          <div>
            <div className={className ? className : classes.unavailable}>
              <Typography variant='body2'>{t('streamUnavailable')}</Typography>
            </div>
            {retry && (
              <div className={className ? className : classes.retryWrapper}>
                <Tooltip title={t('retry')}>
                  <IconButton
                    size='medium'
                    className={className ? className : classes.retryButton}
                    onClick={() => setReload(!reload)}
                  >
                    <Replay fontSize='medium' />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(
  KVDSVideoPlayer,
  (prev, next) =>
    prev.url !== next.url &&
    prev.applicationName !== next.applicationName &&
    prev.streamName !== next.streamName &&
    prev.controls !== next.controls
);
